import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {Link} from 'components/DSYS/Link';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {Sparkline} from 'components/DSYS/Sparkline';
import {logStockClick} from 'views/home/dashboard/dashboardAnalytics';
import {StatTileData} from 'views/home/dashboard/dashboardModel';
import {Tile} from 'views/home/dashboard/Tile';

const STOCK_QUOTE_URL = 'https://www.google.com/finance/quote/DBX:NASDAQ';

const stockTrendClass = (isUp: boolean) =>
  atoms({color: isUp ? 'Success Base - State 2' : 'Alert On Surface'});

const formatStockChange = (change: number, percent: number, isUp: boolean): string => {
  const amount = isUp ? `+${change.toFixed(2)}` : change.toFixed(2);
  return `${amount} (${percent.toFixed(2)}%)`;
};

const StockTileBody = ({data}: {data: StatTileData}) => {
  const isUp = (data.delta ?? 0) >= 0;
  const trendClass = stockTrendClass(isUp);
  const spark = data.spark ?? [];
  const showSpark = spark.length > 5;

  return (
    <Box display="flex" alignItems="center" height="100%" style={{gap: 8}}>
      <Box style={{flex: 1, minWidth: 0}}>
        <Stack gap="4">
          <Box display="flex" alignItems="center" style={{gap: 4}}>
            <Text isBold style={{fontSize: 16}}>
              DBX
            </Text>
            {data.delta !== undefined ? (
              <Text isBold className={trendClass} style={{fontSize: 13}}>
                {isUp ? '▲' : '▼'}
              </Text>
            ) : null}
          </Box>
          {data.caption ? (
            <Text size="small" color="faint">
              {data.caption}
            </Text>
          ) : null}
        </Stack>
      </Box>
      {showSpark ? (
        <Box display="flex" alignItems="center" style={{flexShrink: 0}}>
          <Sparkline
            values={spark}
            trend={isUp ? 'up' : (data.delta ?? 0) === 0 ? 'flat' : 'down'}
            width={107}
            height={41}
          />
        </Box>
      ) : null}
      <Box style={{flexShrink: 0, textAlign: 'right'}}>
        <Stack gap="4">
          <Text isBold style={{fontSize: 16}}>
            {data.value}
          </Text>
          {data.delta !== undefined && typeof data.change === 'number' ? (
            <Box>
              <Text size="small" className={trendClass}>
                {formatStockChange(data.change, data.delta, isUp)}
              </Text>
            </Box>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
};

const StockTileSkeleton = () => (
  <Box display="flex" alignItems="center" height="100%" style={{gap: 8}}>
    <Box style={{flex: 1, minWidth: 0}}>
      <Stack gap="4">
        <Skeleton.Text width={48} />
        <Skeleton.Text size="small" width={88} />
      </Stack>
    </Box>
    <Box style={{flexShrink: 0, textAlign: 'right'}}>
      <Stack gap="4">
        <Skeleton.Text width={56} />
        <Skeleton.Text size="small" width={72} />
      </Stack>
    </Box>
  </Box>
);

export const StatTile = ({data, isLoading}: {data: StatTileData; isLoading?: boolean}) => {
  const bodyRef = useSkeletonFadeIn(isLoading);
  const tile = (
    <Tile label={undefined} interactive bodyRef={bodyRef}>
      {isLoading ? <StockTileSkeleton /> : <StockTileBody data={data} />}
    </Tile>
  );

  return (
    <Link
      hasNoUnderline
      to={STOCK_QUOTE_URL}
      onClick={() => logStockClick()}
      className={atoms({display: 'block', height: '100%'})}
    >
      {tile}
    </Link>
  );
};
